import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import './SettingsForm.css';
import { FaArrowLeft, FaRocket, FaUnlockAlt, FaLanguage, FaCog, FaMapMarkerAlt, FaEnvelope, FaBell, FaBellSlash, FaCalendarAlt, FaPalette, FaCookie, FaMobileAlt, FaCommentDots, FaGlobe, FaLocationArrow, FaExclamationTriangle, FaTimes } from 'react-icons/fa';
import { Capacitor } from '@capacitor/core';
import { CustomButton8, InputPassword1, CustomInputBox1, ModernSwitch, WSButton } from "./Templates.js";
import { FEATURES } from '../config/features';
import { getUserCountry, getCountryOptions, validateCountryChange, formatCountryDisplay } from '../utils/countryUtils';
import { supabase } from './data/supabaseClient';
import { AuthContext } from './Provider';
import { useTranslation } from 'react-i18next';
import Spinner from './common/Spinner';
import { useToast } from './hooks/ToastContext';
import SimpleAccountDelete from './SimpleAccountDelete';
import LocationAlertsSection from './LocationAlerts/LocationAlertsSection';
import ThemeCard from './common/ThemeCard';
import ErrorMessage from './common/ErrorMessage';
import CookiePreferencesModal from './common/CookiePreferencesModal';
import { getConsentSummary } from '../utils/cookieConsentManager';

// Theme configuration with color swatches
const THEME_OPTIONS = [
  {
    id: 'option1',
    nameKey: 'menu.theme_default',
    descriptionKey: 'settings.theme_default_desc',
    colors: {
      primary: '#313a3a',
      background: '#bedbd4',
      accent: '#9FC131',
      secondary: '#5a9dc9',
    }
  },
  {
    id: 'option2',
    nameKey: 'menu.theme_dark',
    descriptionKey: 'settings.theme_dark_desc',
    colors: {
      primary: '#3a3f38',
      background: '#6c868a',
      accent: '#88ad97',
      secondary: '#4eb8b8',
    }
  },
  {
    id: 'option3',
    nameKey: 'menu.theme_light',
    descriptionKey: 'settings.theme_light_desc',
    colors: {
      primary: '#454850',
      background: '#c9c4b9',
      accent: '#7FA028',
      secondary: '#6b9285',
    }
  }
];

const SettingsForm = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('common');
  const { user, updateLanguagePreference, updateThemePreference } = useContext(AuthContext);
  const { addToast } = useToast();
  const locationInputRef = useRef(null);
  const [googleMapsLoaded, setGoogleMapsLoaded] = useState(false);
  
  // Form states
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState(i18n.language || 'en');
  const [selectedTheme, setSelectedTheme] = useState(localStorage.getItem('theme') || 'option1');
  const [locationData, setLocationData] = useState({
    city: '',
    city_lat: null,
    city_lng: null
  });
  const [emailNotificationsEnabled, setEmailNotificationsEnabled] = useState(true);
  const [newsletterEnabled, setNewsletterEnabled] = useState(false);
  const [locationAlertsEnabled, setLocationAlertsEnabled] = useState(false);
  const [alertFrequency, setAlertFrequency] = useState('daily');
  const [savingAlertSettings, setSavingAlertSettings] = useState(false);

  // Push notifications (native app only — web has no push)
  const isNativeApp = Capacitor.isNativePlatform();
  const [pushMessagesEnabled, setPushMessagesEnabled] = useState(true);
  const [pushMatchesEnabled, setPushMatchesEnabled] = useState(true);
  const [pushLoading, setPushLoading] = useState(false);

  // Cookie preferences state
  const [showCookiePreferencesModal, setShowCookiePreferencesModal] = useState(false);
  const [cookieConsent, setCookieConsent] = useState(null);

  // Country (moved here from the Profile page — it's a scope preference).
  const [showCountryChange, setShowCountryChange] = useState(false);
  const [newCountry, setNewCountry] = useState(user?.profile?.country_code || FEATURES.DEFAULT_COUNTRY);
  const [changingCountry, setChangingCountry] = useState(false);

  // UI states
  const [loading, setLoading] = useState(false);
  const [languageLoading, setLanguageLoading] = useState(false);
  const [themeLoading, setThemeLoading] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);
  const [emailNotificationsLoading, setEmailNotificationsLoading] = useState(false);
  const [newsletterLoading, setNewsletterLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    // Set the initial language selection based on i18n
    setSelectedLanguage(i18n.language || 'en');

    // Load current user location and email preferences from profile
    if (user?.profile) {
      setLocationData({
        city: user.profile.city || '',
        city_lat: user.profile.city_lat,
        city_lng: user.profile.city_lng
      });
      // Default to true if not set (new feature)
      setEmailNotificationsEnabled(user.profile.email_notifications_enabled !== false);

      // Load newsletter preference (default to false - opt-in for GDPR)
      setNewsletterEnabled(user.profile.newsletter_enabled === true);

      // Load location alert preferences
      const frequency = user.profile.location_alert_frequency || 'daily';
      setAlertFrequency(frequency);
      setLocationAlertsEnabled(frequency !== 'never');

      // Push notification preferences (default on)
      setPushMessagesEnabled(user.profile.push_messages_enabled !== false);
      setPushMatchesEnabled(user.profile.push_matches_enabled !== false);
    }

    // Load cookie consent summary
    const consentSummary = getConsentSummary();
    setCookieConsent(consentSummary);

    // Check if Google Maps is loaded
    const checkGoogleMapsLoaded = () => {
      if (window.google && window.google.maps) {
        setGoogleMapsLoaded(true);
        // Initialize autocomplete after maps is loaded
        setTimeout(() => {
          initializeLocationAutocomplete();
        }, 100);
      } else {
        setTimeout(checkGoogleMapsLoaded, 500);
      }
    };

    checkGoogleMapsLoaded();
  }, [i18n.language, user]);

  const handleBackClick = () => {
    navigate(-1);
  };
  
  // Initialize Google Places Autocomplete for location
  const initializeLocationAutocomplete = useCallback(() => {
    if (!locationInputRef.current || !googleMapsLoaded) return;
    
    try {
      const autocomplete = new window.google.maps.places.Autocomplete(
        locationInputRef.current.querySelector('input'),
        {
          types: ['(cities)'],
          // Remove country restriction to allow international cities
        }
      );
      
      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (place.geometry) {
          setLocationData({
            city: place.formatted_address,
            city_lat: place.geometry.location.lat(),
            city_lng: place.geometry.location.lng()
          });
        }
      });
    } catch (error) {
      console.error('Error initializing location autocomplete:', error);
    }
  }, [googleMapsLoaded]);
  
  // Get user's current location
  const detectCurrentLocation = async () => {
    setLocationLoading(true);
    
    try {
      if (!navigator.geolocation) {
        throw new Error(t('profile_setup.geolocation_not_supported'));
      }
      
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(
          resolve,
          reject,
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 300000
          }
        );
      });
      
      const { latitude, longitude } = position.coords;
      
      if (window.google && window.google.maps) {
        const geocoder = new window.google.maps.Geocoder();
        const latLng = new window.google.maps.LatLng(latitude, longitude);
        
        geocoder.geocode({ location: latLng }, (results, status) => {
          if (status === 'OK' && results[0]) {
            const cityComponent = results[0].address_components.find(
              component => component.types.includes('locality') || 
                          component.types.includes('administrative_area_level_1')
            );
            
            const countryComponent = results[0].address_components.find(
              component => component.types.includes('country')
            );
            
            let locationName = results[0].formatted_address;
            
            if (cityComponent && countryComponent) {
              locationName = `${cityComponent.long_name}, ${countryComponent.long_name}`;
            } else if (countryComponent) {
              const adminArea = results[0].address_components.find(
                component => component.types.includes('administrative_area_level_1')
              );
              if (adminArea) {
                locationName = `${adminArea.long_name}, ${countryComponent.long_name}`;
              } else {
                locationName = countryComponent.long_name;
              }
            }
            
            setLocationData({
              city: locationName,
              city_lat: latitude,
              city_lng: longitude
            });
            
            addToast({
              type: 'success',
              title: t('settings.success'),
              description: t('profile_setup.location_detection_info')
            });
          } else {
            throw new Error(t('profile_setup.geocoding_failed'));
          }
        });
      } else {
        // Fallback: just store coordinates
        setLocationData(prev => ({
          ...prev,
          city_lat: latitude,
          city_lng: longitude
        }));
      }
    } catch (error) {
      console.error('Location detection error:', error);
      let errorMessage = t('profile_setup.location_detection_failed');
      
      if (error.code === 1) {
        errorMessage = t('profile_setup.location_permission_denied');
      } else if (error.code === 2) {
        errorMessage = t('profile_setup.location_unavailable');
      } else if (error.code === 3) {
        errorMessage = t('profile_setup.location_timeout');
      }
      
      addToast({
        type: 'error',
        title: t('settings.error'),
        description: errorMessage
      });
    } finally {
      setLocationLoading(false);
    }
  };
  
  // Save location to profile
  const handleLocationSave = async () => {
    if (!user?.id || !locationData.city) {
      addToast({
        type: 'error',
        title: t('settings.error'),
        description: t('errors.city_required')
      });
      return;
    }
    
    try {
      setLocationLoading(true);
      
      const { error } = await supabase
        .from('profiles')
        .update({
          city: locationData.city,
          city_lat: locationData.city_lat,
          city_lng: locationData.city_lng,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);
      
      if (error) throw error;
      
      addToast({
      type: 'success',
      title: t('settings.success'),
      description: t('settings.location_updated')
      });
      
      // Refresh the page to update the context
      setTimeout(() => {
        window.location.reload();
      }, 1000);
      
    } catch (error) {
      console.error('Error updating location:', error);
      addToast({
      type: 'error',
      title: t('settings.error'),
      description: t('settings.location_update_failed')
      });
    } finally {
      setLocationLoading(false);
    }
  };

  // Change country (moved from Profile). Updates the profile, then reloads so
  // country-dependent UI (maps, defaults) picks up the change — mirroring how
  // location save already refreshes the app.
  const handleCountryChange = async () => {
    if (!newCountry || newCountry === user?.profile?.country_code) {
      setShowCountryChange(false);
      return;
    }
    const validation = validateCountryChange(user?.profile?.country_code, newCountry);
    if (validation && validation.success === false) {
      addToast({ type: 'error', title: t('settings.error'), description: validation.message });
      return;
    }
    try {
      setChangingCountry(true);
      const { error } = await supabase
        .from('profiles')
        .update({ country_code: newCountry, updated_at: new Date().toISOString() })
        .eq('id', user.id);
      if (error) throw error;

      addToast({
        type: 'success',
        title: t('settings.success'),
        description: t('profile.country_changed_success', { country: formatCountryDisplay(newCountry) })
      });
      setShowCountryChange(false);
      setTimeout(() => window.location.reload(), 1000);
    } catch (error) {
      console.error('Country change failed:', error);
      addToast({ type: 'error', title: t('settings.error'), description: t('settings.unknown_error') });
    } finally {
      setChangingCountry(false);
    }
  };

  const validatePasswordForm = () => {
    // Reset error and success states
    setError(null);
    setSuccessMessage('');

    // Validate inputs
    if (!currentPassword) {
      setError(t('settings.current_password_required'));
      return false;
    }

    if (!newPassword) {
      setError(t('settings.new_password_required'));
      return false;
    }

    if (newPassword.length < 6) {
      setError(t('settings.password_length'));
      return false;
    }

    if (newPassword !== confirmPassword) {
      setError(t('settings.passwords_dont_match'));
      return false;
    }

    return true;
  };

  const handlePasswordChange = async (event) => {
    event.preventDefault();
    
    if (!validatePasswordForm()) {
      return;
    }

    try {
      setLoading(true);

      // First verify the current password is correct
      const { data: { user }, error: sessionError } = await supabase.auth.getUser();
      
      if (sessionError) {
        throw new Error(t('settings.auth_error'));
      }

      // Update password
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (updateError) {
        throw new Error(updateError.message);
      }

      // Success
      setSuccessMessage(t('settings.password_updated'));
      addToast({
        type: 'success',
        title: t('settings.success'),
        description: t('settings.password_updated')
      });

      // Clear form
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');

    } catch (error) {
      console.error('Error changing password:', error);
      setError(error.message || t('settings.unknown_error'));
      
      addToast({
        type: 'error',
        title: t('settings.error'),
        description: error.message || t('settings.password_change_failed')
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLanguageChange = async (e) => {
    const newLanguage = e.target.value;
    
    try {
      setLanguageLoading(true);
      setSelectedLanguage(newLanguage);
      
      // First update i18n
      await i18n.changeLanguage(newLanguage);
      
      // Then update user preference if logged in
      if (updateLanguagePreference) {
        const success = await updateLanguagePreference(newLanguage);
        
        if (success) {
          addToast({
            type: 'success',
            title: t('settings.success'),
            description: t('settings.language_updated')
          });
        } else {
          console.warn('Language preference not saved to profile');
        }
      }
    } catch (error) {
      console.error('Error changing language:', error);
      addToast({
        type: 'error',
        title: t('settings.error'),
        description: t('settings.language_update_failed')
      });
    } finally {
      setLanguageLoading(false);
    }
  };

  // Handle theme change
  const handleThemeChange = async (newTheme) => {
    if (themeLoading || newTheme === selectedTheme) return;

    try {
      setThemeLoading(true);
      setSelectedTheme(newTheme);

      // Update theme preference (applies theme immediately and saves to profile)
      if (updateThemePreference) {
        const success = await updateThemePreference(newTheme);

        if (success) {
          addToast({
            type: 'success',
            title: t('settings.success'),
            description: t('settings.theme_updated')
          });
        } else {
          console.warn('Theme preference not saved to profile');
        }
      }
    } catch (error) {
      console.error('Error changing theme:', error);
      addToast({
        type: 'error',
        title: t('settings.error'),
        description: t('settings.error')
      });
    } finally {
      setThemeLoading(false);
    }
  };

  // Handle email notifications toggle
  const handleEmailNotificationsToggle = async (enabled) => {
    if (!user?.id) {
      addToast({
        type: 'error',
        title: t('settings.error'),
        description: t('errors.error_fetch_user')
      });
      return;
    }
    
    try {
      setEmailNotificationsLoading(true);
      
      const { error } = await supabase
        .from('profiles')
        .update({
          email_notifications_enabled: enabled,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);
      
      if (error) throw error;
      
      setEmailNotificationsEnabled(enabled);
      
      addToast({
        type: 'success',
        title: t('settings.success'),
        description: enabled 
          ? t('settings.email_notifications_enabled')
          : t('settings.email_notifications_disabled')
      });
      
    } catch (error) {
      console.error('Error updating email notifications:', error);
      addToast({
        type: 'error',
        title: t('settings.error'),
        description: t('settings.email_notifications_update_failed')
      });
      // Revert toggle state on error
      setEmailNotificationsEnabled(!enabled);
    } finally {
      setEmailNotificationsLoading(false);
    }
  };

  // Handle a push-notification preference toggle (column: push_messages_enabled | push_matches_enabled)
  const handlePushToggle = async (column, enabled, setLocal) => {
    if (!user?.id) return;
    setLocal(enabled);
    try {
      setPushLoading(true);
      const { error } = await supabase
        .from('profiles')
        .update({ [column]: enabled, updated_at: new Date().toISOString() })
        .eq('id', user.id);
      if (error) throw error;
    } catch (error) {
      console.error('Error updating push preference:', error);
      addToast({
        type: 'error',
        title: t('settings.error'),
        description: t('settings.push_update_failed') || 'Could not update push setting'
      });
      setLocal(!enabled); // revert on error
    } finally {
      setPushLoading(false);
    }
  };

  // Handle newsletter toggle
  const handleNewsletterToggle = async (enabled) => {
    if (!user?.id) {
      addToast({
        type: 'error',
        title: t('settings.error'),
        description: t('errors.error_fetch_user')
      });
      return;
    }

    try {
      setNewsletterLoading(true);

      const updateData = {
        newsletter_enabled: enabled,
        updated_at: new Date().toISOString()
      };

      // If enabling, set marketing consent date
      if (enabled) {
        updateData.marketing_consent_date = new Date().toISOString();
      }

      const { error } = await supabase
        .from('profiles')
        .update(updateData)
        .eq('id', user.id);

      if (error) throw error;

      setNewsletterEnabled(enabled);

      addToast({
        type: 'success',
        title: t('settings.success'),
        description: enabled
          ? t('settings.newsletter_enabled')
          : t('settings.newsletter_disabled')
      });

    } catch (error) {
      console.error('Error updating newsletter preference:', error);
      addToast({
        type: 'error',
        title: t('settings.error'),
        description: t('settings.newsletter_update_failed')
      });
      // Revert toggle state on error
      setNewsletterEnabled(!enabled);
    } finally {
      setNewsletterLoading(false);
    }
  };

  // Handle location alerts enable/disable
  const handleLocationAlertsToggle = async (enabled) => {
    if (!user?.id) return;
    
    try {
      setSavingAlertSettings(true);
      
      const newFrequency = enabled ? alertFrequency : 'never';
      
      const { error } = await supabase
        .from('profiles')
        .update({
          location_alert_frequency: newFrequency,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);
      
      if (error) throw error;
      
      setLocationAlertsEnabled(enabled);
      
      addToast({
        type: 'success',
        title: t('settings.success'),
        description: enabled 
          ? (t('settings.location_alerts_enabled') || 'Location alerts enabled')
          : (t('settings.location_alerts_disabled') || 'Location alerts disabled')
      });
      
    } catch (error) {
      console.error('Error updating location alerts:', error);
      addToast({
        type: 'error',
        title: t('errors.error'),
        description: t('settings.location_alerts_update_failed') || 'Failed to update location alerts'
      });
      setLocationAlertsEnabled(!enabled);
    } finally {
      setSavingAlertSettings(false);
    }
  };

  // Handle alert frequency change (instant/daily)
  const handleAlertFrequencyChange = async (newFrequency) => {
    if (!user?.id) return;

    try {
      setSavingAlertSettings(true);

      const { error } = await supabase
        .from('profiles')
        .update({
          location_alert_frequency: newFrequency,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (error) throw error;

      setAlertFrequency(newFrequency);

      addToast({
        type: 'success',
        title: t('settings.success'),
        description: newFrequency === 'instant'
          ? (t('settings.switched_to_instant') || 'Switched to instant alerts')
          : (t('settings.switched_to_daily') || 'Switched to daily digest')
      });

    } catch (error) {
      console.error('Error updating alert frequency:', error);
      addToast({
        type: 'error',
        title: t('errors.error'),
        description: t('settings.alert_frequency_update_failed') || 'Failed to update alert frequency'
      });
    } finally {
      setSavingAlertSettings(false);
    }
  };

  // Handle cookie preferences modal
  const handleCookiePreferencesClick = () => {
    setShowCookiePreferencesModal(true);
  };

  const handleCookieModalClose = (consentGiven) => {
    setShowCookiePreferencesModal(false);
    if (consentGiven) {
      // Reload consent summary
      const consentSummary = getConsentSummary();
      setCookieConsent(consentSummary);
    }
  };

  return (
    <div className="settings-container">
        <div className="back-button">
          <CustomButton8 onClick={handleBackClick}>
            <FaArrowLeft/>
          </CustomButton8>
        </div>

        <h2 className="settings-page-title">{t('navigation.settings')}</h2>

        {/* Location Settings Section */}
        <div className="settings-section">
          <div className="section-header">
            <FaMapMarkerAlt />
            <h3>{t('settings.location_settings')}</h3>
          </div>

          <div className="form-group" ref={locationInputRef}>
            <label htmlFor="location">{t('settings.your_location')}:</label>
            <CustomInputBox1
              className="wsform-input"
              value={locationData.city}
              onChange={(e) => setLocationData(prev => ({ ...prev, city: e.target.value }))}
              placeholder={t('settings.city_placeholder')}
              disabled={locationLoading || !googleMapsLoaded}
            />
            <div className="location-actions">
              <WSButton
                variant="secondary"
                onClick={detectCurrentLocation}
                disabled={locationLoading}
                label={t('settings.detect_location')}
                icon={locationLoading ? <Spinner size="small" showText={false} thickness={2} /> : <FaLocationArrow />}
              />
              <WSButton
                variant="primary"
                onClick={handleLocationSave}
                disabled={locationLoading || !locationData.city}
                label={locationLoading ? t('settings.saving') : t('settings.save_location')}
                icon={locationLoading ? <Spinner size="small" showText={false} thickness={2} /> : null}
              />
            </div>
            <p className="help-text">{t('settings.location_help_text')}</p>
          </div>

          {/* Favorite Locations for Alerts */}
          <LocationAlertsSection />
        </div>

        {/* Country Section — moved here from the Profile page (scope preference). */}
        {FEATURES.MULTI_COUNTRY_ENABLED && (
          <div className="settings-section">
            <div className="section-header">
              <FaGlobe />
              <h3>{t('profile.current_country')}</h3>
            </div>

            <div className="form-group">
              <p className="settings-current-country">
                {formatCountryDisplay(getUserCountry({ profile: user?.profile }))}
              </p>

              {!showCountryChange ? (
                <WSButton
                  variant="secondary"
                  onClick={() => setShowCountryChange(true)}
                  label={t('profile.change_country')}
                  icon={<FaGlobe />}
                />
              ) : (
                <>
                  <div className="select-container">
                    <select
                      value={newCountry}
                      onChange={(e) => setNewCountry(e.target.value)}
                      disabled={changingCountry}
                      className="language-select"
                    >
                      {getCountryOptions().map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <p className="help-text">{t('profile.country_change_warning')}</p>
                  <div className="location-actions">
                    <WSButton
                      variant="primary"
                      onClick={handleCountryChange}
                      disabled={changingCountry || newCountry === user?.profile?.country_code}
                      label={changingCountry ? t('app.saving') : t('buttons.save')}
                      icon={changingCountry ? <Spinner size="small" showText={false} /> : null}
                    />
                    <WSButton
                      variant="secondary"
                      onClick={() => {
                        setShowCountryChange(false);
                        setNewCountry(user?.profile?.country_code || FEATURES.DEFAULT_COUNTRY);
                      }}
                      disabled={changingCountry}
                      ariaLabel={t('buttons.cancel')}
                      icon={<FaTimes />}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Notification Settings Section */}
        <div className="settings-section">
          <div className="section-header">
            <FaEnvelope />
            <h3>{t('settings.notification_settings')}</h3>
          </div>
          
          <p className="section-description">
            {t('settings.email_notifications_description')}
          </p>
          
          <ModernSwitch
            label={emailNotificationsEnabled ? t('settings.enabled') : t('settings.disabled')}
            checked={emailNotificationsEnabled}
            onChange={(e) => handleEmailNotificationsToggle(e.target.checked)}
            disabled={emailNotificationsLoading}
            leftIcon={<FaBellSlash />}
            rightIcon={<FaBell />}
            className={emailNotificationsLoading ? 'loading' : ''}
          />

          {/* Newsletter Settings */}
          <div className="newsletter-settings" style={{ marginTop: '30px' }}>
            <div className="newsletter-settings-header">
              <h4>📰 {t('settings.newsletter')}</h4>
              <p className="help-text">{t('settings.newsletter_help')}</p>
            </div>

            <ModernSwitch
              label={newsletterEnabled ? t('settings.enabled') : t('settings.disabled')}
              checked={newsletterEnabled}
              onChange={(e) => handleNewsletterToggle(e.target.checked)}
              disabled={newsletterLoading}
              leftIcon={<FaBellSlash />}
              rightIcon={<FaBell />}
              className={newsletterLoading ? 'loading' : ''}
            />
          </div>

          {/* Location Alerts Settings */}
          <div className="location-alerts-settings">
            <div className="alert-settings-header">
              <h4>📍 {t('settings.location_alerts')}</h4>
              <p className="help-text">{t('settings.location_alerts_help')}</p>
            </div>
            
            <ModernSwitch
              label={locationAlertsEnabled ? t('settings.enabled') : t('settings.disabled')}
              checked={locationAlertsEnabled}
              onChange={(e) => handleLocationAlertsToggle(e.target.checked)}
              disabled={savingAlertSettings}
              leftIcon={<FaBellSlash />}
              rightIcon={<FaBell />}
              className={savingAlertSettings ? 'loading' : ''}
            />

            {locationAlertsEnabled && (
              <div className="alert-frequency-control">
                <p className="frequency-help-text">
                  {alertFrequency === 'instant'
                    ? '⚡ ' + t('settings.instant_alerts_help')
                    : '📅 ' + t('settings.daily_alerts_help')}
                </p>
                <ModernSwitch
                  label={alertFrequency === 'instant' ? t('settings.instant') : t('settings.daily')}
                  checked={alertFrequency === 'instant'}
                  onChange={(e) => handleAlertFrequencyChange(e.target.checked ? 'instant' : 'daily')}
                  disabled={savingAlertSettings}
                  leftIcon={<FaCalendarAlt />}
                  rightIcon={<FaRocket />}
                  className={savingAlertSettings ? 'loading' : ''}
                />
              </div>
            )}
          </div>
        </div>

        {/* Push notifications — native app only (web has no push) */}
        {isNativeApp && (
          <div className="settings-section">
            <div className="section-header">
              <FaMobileAlt />
              <h3>{t('settings.push_notifications')}</h3>
            </div>

            <p className="section-description">
              {t('settings.push_notifications_description')}
            </p>

            <div className="push-setting">
              <div className="push-setting-header">
                <h4><FaCommentDots /> {t('settings.push_new_messages')}</h4>
                <p className="help-text">{t('settings.push_new_messages_help')}</p>
              </div>
              <ModernSwitch
                label={pushMessagesEnabled ? t('settings.enabled') : t('settings.disabled')}
                checked={pushMessagesEnabled}
                onChange={(e) => handlePushToggle('push_messages_enabled', e.target.checked, setPushMessagesEnabled)}
                disabled={pushLoading}
                leftIcon={<FaBellSlash />}
                rightIcon={<FaBell />}
                className={pushLoading ? 'loading' : ''}
              />
            </div>

            <div className="push-setting">
              <div className="push-setting-header">
                <h4><FaMapMarkerAlt /> {t('settings.push_new_matches')}</h4>
                <p className="help-text">{t('settings.push_new_matches_help')}</p>
              </div>
              <ModernSwitch
                label={pushMatchesEnabled ? t('settings.enabled') : t('settings.disabled')}
                checked={pushMatchesEnabled}
                onChange={(e) => handlePushToggle('push_matches_enabled', e.target.checked, setPushMatchesEnabled)}
                disabled={pushLoading}
                leftIcon={<FaBellSlash />}
                rightIcon={<FaBell />}
                className={pushLoading ? 'loading' : ''}
              />
            </div>
          </div>
        )}

        <div className="settings-section">
          <div className="section-header">
            <FaUnlockAlt />
            <h3>{t('settings.change_password')}</h3>
          </div>

          <form onSubmit={handlePasswordChange}>
            <div className="form-group">
              <label className="password-label">{t('settings.current_password')}</label>
              <InputPassword1
                id="currentPassword"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label className="password-label">{t('settings.new_password')}</label>
              <InputPassword1
                id="newPassword"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label className="password-label">{t('settings.confirm_password')}</label>
              <InputPassword1
                id="confirmPassword"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={loading}
              />
            </div>

            {error && <ErrorMessage message={error} type="error" variant="block" />}
            {successMessage && <div className="success-message">{successMessage}</div>}

            <div className="form-actions">
              <WSButton
                type="submit"
                variant="primary"
                label={loading ? t('settings.saving') : t('settings.update_password')}
                disabled={loading}
                icon={loading ? <Spinner size="small" showText={false} thickness={2} /> : null}
              />
            </div>
          </form>
        </div>

        <div className="settings-section">
          <div className="section-header">
            <FaLanguage />
            <h3>{t('settings.language_settings')}</h3>
          </div>

          <div className="form-group">
            <label htmlFor="language">{t('settings.language')}:</label>
            <div className="select-container">
              <select
                id="language"
                value={selectedLanguage}
                onChange={handleLanguageChange}
                disabled={languageLoading}
                className="language-select"
              >
                <option value="en">{t('languages.english')}</option>
                <option value="lv">{t('languages.latvian')}</option>
                <option value="ru">{t('languages.russian')}</option>
              </select>
              {languageLoading && (
                <div className="select-loading">
                  <Spinner size="small" showText={false} thickness={2} />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Theme Settings Section */}
        <div className="settings-section">
          <div className="section-header">
            <FaPalette />
            <h3>{t('settings.theme_settings')}</h3>
          </div>

          <div className="form-group">
            <label>{t('settings.select_theme')}:</label>
            <p className="help-text">{t('settings.theme_help_text')}</p>

            <div className="theme-selector-grid">
              {THEME_OPTIONS.map((theme) => (
                <ThemeCard
                  key={theme.id}
                  themeId={theme.id}
                  name={t(theme.nameKey)}
                  description={t(theme.descriptionKey)}
                  colors={theme.colors}
                  isSelected={selectedTheme === theme.id}
                  onClick={handleThemeChange}
                  disabled={themeLoading}
                />
              ))}
            </div>

            {themeLoading && (
              <div className="theme-loading">
                <Spinner size="small" showText={false} thickness={2} />
                <span>{t('settings.applying_theme')}</span>
              </div>
            )}
          </div>
        </div>

        {/* Cookie Preferences Section */}
        <div className="settings-section">
          <div className="section-header">
            <FaCookie />
            <h3>{t('settings.cookie_preferences')}</h3>
          </div>

          <p className="section-description">
            {t('settings.cookie_preferences_description')}
          </p>

          {cookieConsent && (
            <div className="cookie-consent-summary">
              <div className="consent-stats">
                <div className="stat-item">
                  <span className="stat-label">{t('settings.cookies_enabled')}:</span>
                  <span className="stat-value">
                    {cookieConsent.enabledCount} / {cookieConsent.totalCount}
                  </span>
                </div>
                {cookieConsent.lastUpdated && (
                  <div className="stat-item">
                    <span className="stat-label">{t('settings.last_updated')}:</span>
                    <span className="stat-value">
                      {new Date(cookieConsent.lastUpdated).toLocaleDateString(i18n.language)}
                    </span>
                  </div>
                )}
              </div>

              <div className="enabled-categories">
                <p className="categories-label">{t('settings.enabled_categories')}:</p>
                <div className="category-badges">
                  {cookieConsent.enabledCategories.map((category) => (
                    <span key={category} className="category-badge">
                      {t(`cookieBanner.category_${category}`)}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className="cookie-actions">
            <WSButton
              variant="secondary"
              onClick={handleCookiePreferencesClick}
              label={t('settings.manage_cookie_preferences')}
              icon={<FaCookie />}
            />
            <a href="/cookies" className="cookie-policy-link">
              {t('settings.view_cookie_policy')} →
            </a>
          </div>
        </div>

        {/* DANGER ZONE */}
        <div className="settings-section danger-zone">
          <div className="section-header">
            <FaExclamationTriangle />
            <h3>{t('settings.danger_zone')}</h3>
          </div>
          <p className="danger-zone-description">
            {t('settings.danger_zone_description')}
          </p>
          
          <div className="danger-item">
            <div className="danger-item-info">
              <h4>{t('settings.delete_account')}</h4>
              <p>{t('settings.delete_account_soft_description')}</p>
            </div>
            <SimpleAccountDelete />
          </div>
          
          <div className="danger-item data-deletion-info">
            <div className="danger-item-info">
              <h4>{t('settings.complete_data_deletion')}</h4>
              <p>{t('settings.complete_data_deletion_description')}</p>
              <p className="contact-support">
                {t('settings.contact_support_text')}{' '}
                <a href="mailto:support@wayshare.app" className="support-email">
                  support@wayshare.app
                </a>
              </p>
            </div>
          </div>
        </div>

        {/* Cookie Preferences Modal */}
        {showCookiePreferencesModal && (
          <CookiePreferencesModal
            isOpen={showCookiePreferencesModal}
            onClose={handleCookieModalClose}
          />
        )}
      </div>
  );
};

export default SettingsForm;
